import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import "../App.css";

const Navbar = ({ user, onLogout }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const dropdownRef = useRef(null);
  const dropdownBtnRef = useRef(null);
  const mobileRef = useRef(null);

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 180 });

  const navigate = useNavigate();

  // Close dropdown + mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const closeMenusAndNavigate = (path) => {
    setMobileOpen(false);
    setDropdownOpen(false);
    navigate(path);
  };

  // When dropdown opens (or window scroll/resize), compute fixed position for portal menu
  useEffect(() => {
    if (!dropdownOpen) return;

    const compute = () => {
      const btn = dropdownBtnRef.current;
      if (!btn) return;

      const r = btn.getBoundingClientRect();
      const width = Math.max(160, r.width); // keep it at least 160px wide

      setDropdownPos({
        top: r.bottom + 8,             // 8px gap below button
        left: r.right - width,         // right-aligned to button
        width,
      });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true); // true = catch nested scroll containers

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [dropdownOpen]);

  return (
    <nav className="navbar navbar--responsive">
      {/* Left: Brand */}
      <div className="navbar-left">
        <div
          className="navbar-brand"
          onClick={() => closeMenusAndNavigate("/")}
          role="button"
          tabIndex={0}
        >
          <label>MySnap</label>
        </div>

        {/* Desktop links */}
        <div className="navbar-links navbar-links--desktop">
          <Link className="button" to="/cards">Archetypes</Link>
          <Link className="button" to="/decks">Decks</Link>
          <Link className="button" to="/rankings">Rankings</Link>
          <Link className="button" to="/variants">Variants</Link>
          <Link className="button" to="/shared-decks">Shared</Link>
        </div>
      </div>

      {/* Right: Mobile hamburger + User */}
      <div className="navbar-right">
        {/* Mobile hamburger */}
        <div ref={mobileRef} className="navbar-mobile">
          <button
            className="button_selected navbar-hamburger"
            onClick={() => setMobileOpen((p) => !p)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            ☰
          </button>

          {mobileOpen && (
            <div className="navbar-mobile-menu">
              <Link className="navbar-mobile-link" to="/cards" onClick={() => setMobileOpen(false)}>Archetypes</Link>
              <Link className="navbar-mobile-link" to="/decks" onClick={() => setMobileOpen(false)}>Decks</Link>
              <Link className="navbar-mobile-link" to="/rankings" onClick={() => setMobileOpen(false)}>Rankings</Link>
              <Link className="navbar-mobile-link" to="/variants" onClick={() => setMobileOpen(false)}>Variants</Link>
              <Link className="navbar-mobile-link" to="/shared-decks" onClick={() => setMobileOpen(false)}>Shared</Link>
            </div>
          )}
        </div>

        {/* User dropdown */}
        {user && (
          <div style={{ position: "relative" }}>
            <button
              ref={dropdownBtnRef}
              className="button_selected"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              {user.username}
            </button>

            {dropdownOpen &&
              createPortal(
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed", // key: not clipped by navbar
                    left: dropdownPos.left,
                    top: dropdownPos.top,
                    width: dropdownPos.width,
                    background: "#111",
                    border: "1px solid rgb(197, 176, 55)",
                    borderRadius: "8px",
                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                    display: "flex",
                    flexDirection: "column",
                    zIndex: 999999, // above everything
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      setMobileOpen(false);
                      onLogout();
                      navigate("/");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "rgb(197, 176, 55)",
                      padding: "12px 12px",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "1rem",
                    }}
                  >
                    
                    Logout
                  </button>
                </div>,
                document.body
              )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
